import { ModelDisplay, StrapiOptions, VideoDisplay } from "@/shared";
import { ALPHABETHICAL, NEWEST, RATING, OLDEST, CUP_ORDER } from '@/constants';

export type MeasurementRange = [number, number] | null;
export type MeasurementFilters = {
  height: MeasurementRange;
  weight: MeasurementRange;
  band: MeasurementRange;
  cup: MeasurementRange;
  waist: MeasurementRange;
  hips: MeasurementRange;
};

export const EMPTY_MEASUREMENTS: MeasurementFilters = {
  height: null, weight: null, band: null, cup: null, waist: null, hips: null,
};

export function parseCup(cup: string | null): { band: number | null; letter: string | null } {
  if (!cup) return { band: null, letter: null, };
  const match = cup.match(/^(\d+)([A-Za-z]+)$/);
  if (!match) return { band: null, letter: null, };
  return { band: parseInt(match[1], 10), letter: match[2].toUpperCase(), };
}

export function addOptionsFromSearchParams(
  type: string,
  searchParams: URLSearchParams,
  options: StrapiOptions = { filters: {}, }
) {
  const modelSort = searchParams.get(`ms`);
  const videoSort = searchParams.get(`vs`);
  const query = searchParams.get(`q`);
  const queryParts = query && query.split(` `);
  const videoTagsParam = searchParams.get(`vt`);
  const videoTags = videoTagsParam?.split(`,`) || [];
  const modelTagsParam = searchParams.get(`mt`);
  const modelTags = modelTagsParam?.split(`,`) || [];

  switch (type) {
  case `video`:
  case `video-info`:
    if (videoTags.length > 0) {
      options.filters.$and = [];
      videoTags.forEach(tag => {
        options.filters.$and.push({
          tags : {
            name: {
              $eq: tag,
            },
          },
        });
      });
    }
    if (videoSort) {
      switch (videoSort) {
      case NEWEST:
        options.sort = [`release:desc`,];
        break;
      case OLDEST:
        options.sort = [`release:asc`,];
        break;
      case RATING:
        options.sort = [`rating:desc`,];
        break;
      case ALPHABETHICAL:
        options.sort = [`slug:asc`,];
        break;

      default:
        throw new Error(`Unknown video sort name ${videoSort}`);
      }
    }
    if (query && queryParts) {
      if (!options.filters.$and) options.filters.$and = [];
      queryParts.forEach((part) => {
        options.filters.$and.push({
          $or: [
            { tags: { name: { $containsi: part, }, }, },
            { title: { $containsi: part, }, },
          ],
        });
      });
    }
    return options;

  case `model`:
    if (modelTags.length > 0) {
      options.filters.$and = [];
      modelTags.forEach(tag => {
        options.filters.$and.push({
          tags : {
            name: {
              $eq: tag,
            },
          },
        });
      });
    }
    if (modelSort) {
      switch (modelSort) {
      case NEWEST:
        options.sort = [`videos.release:desc`,];
        break;
      case OLDEST:
        options.sort = [`videos.release:asc`,];
        break;
      case ALPHABETHICAL:
        options.sort = [`slug:asc`,];
        break;

      default:
        throw new Error(`Unknown model sort name ${modelSort}`);
      }
    }
    if (query && queryParts) {
      if (!options.filters.$and) options.filters.$and = [];
      queryParts.forEach((part) => {
        options.filters.$and.push({
          $or: [
            { tags: { name: { $containsi: part, }, }, },
            { name: { $containsi: part, }, },
            { aliases: { name: { $containsi: part, }, }, },
          ],
        });
      });
    }
    return options;

  default:
    throw new Error(`unknown list element type (${type})`);
  }
}

export function extractText(content: any): string {
  if (!content) return ``;
  function walk(node: any): string {
    if (typeof node.text === `string`) return node.text;
    if (Array.isArray(node.children)) return node.children.map(walk).join(` `);
    return ``;
  }
  return content.map(walk).join(` `).toLowerCase();
}

function filterBySearch<T extends { tags?: { name: string }[] }>(
  items: T[],
  getName: (item: T) => string,
  getAliases: (item: T) => string[],
  getDescription: (item: T) => any,
  query: string,
  tags: string[]
): { item: T; score: number }[] {
  let filtered = items;
  if (tags.length > 0)
    filtered = filtered.filter(item => tags.every(tag => item.tags?.some(t => t.name === tag)));
  if (!query) return filtered.map(item => ({ item, score: 0, }));
  return filtered
    .map(item => ({ item, score: score(getName(item), getAliases(item), item.tags, getDescription(item), query), }))
    .filter(r => r.score > 0);
}

export function filterVideos(
  videos: VideoDisplay[],
  query: string,
  sort: string,
  tags: string[]
): VideoDisplay[] {
  const scored = filterBySearch(videos, v => v.title, () => [], v => v.description, query, tags);
  scored.sort((a, b) => {
    if (query) {
      const d = b.score - a.score;
      if (d !== 0) return d;
    }
    switch (sort) {
    case `newest`: return new Date(b.item.release).getTime() - new Date(a.item.release).getTime();
    case `oldest`: return new Date(a.item.release).getTime() - new Date(b.item.release).getTime();
    case `rating`: return (b.item.rating ?? 0) - (a.item.rating ?? 0);
    case `alphabethical`: return a.item.slug.localeCompare(b.item.slug);
    default: return 0;
    }
  });
  return scored.map(s => s.item);
}

function applyMeasurementFilters(models: ModelDisplay[], measurements: MeasurementFilters): ModelDisplay[] {
  return models.filter((m) => {
    if (measurements.height && (m.height == null || m.height < measurements.height[0] || m.height > measurements.height[1])) return false;
    if (measurements.weight && (m.weight == null || m.weight < measurements.weight[0] || m.weight > measurements.weight[1])) return false;
    if (measurements.waist && (m.waist == null || m.waist < measurements.waist[0] || m.waist > measurements.waist[1])) return false;
    if (measurements.hips && (m.hips == null || m.hips < measurements.hips[0] || m.hips > measurements.hips[1])) return false;
    if (measurements.band || measurements.cup) {
      const parsed = parseCup(m.cup);
      if (measurements.band && (parsed.band == null || parsed.band < measurements.band[0] || parsed.band > measurements.band[1])) return false;
      if (measurements.cup) {
        const idx = parsed.letter ? CUP_ORDER.indexOf(parsed.letter) : -1;
        if (idx === -1 || idx < measurements.cup[0] || idx > measurements.cup[1]) return false;
      }
    }
    return true;
  });
}

export function filterModels(
  models: ModelDisplay[],
  query: string,
  sort: string,
  tags: string[],
  measurements: MeasurementFilters = EMPTY_MEASUREMENTS
): ModelDisplay[] {
  const measured = applyMeasurementFilters(models, measurements);
  const scored = filterBySearch(measured, m => m.name, m => m.aliases, m => m.description, query, tags);
  scored.sort((a, b) => {
    if (query) {
      const d = b.score - a.score;
      if (d !== 0) return d;
    }
    switch (sort) {
    case `newest`: {
      const aT = a.item.firstVideoRelease ? new Date(a.item.firstVideoRelease).getTime() : 0;
      const bT = b.item.firstVideoRelease ? new Date(b.item.firstVideoRelease).getTime() : 0;
      return bT - aT;
    }
    case `alphabethical`: return a.item.slug.localeCompare(b.item.slug);
    default: return 0;
    }
  });
  return scored.map(s => s.item);
}

function scoreWord(word: string, names: string[], tags: { name: string }[] | undefined, desc: string): number {
  if (names.some(n => n === word)) return 5;
  if (names.some(n => n.startsWith(word))) return 4;
  if (names.some(n => n.includes(word))) return 3;
  if (tags?.some(t => t.name.toLowerCase() === word)) return 2.5;
  if (tags?.some(t => t.name.toLowerCase().startsWith(word))) return 2;
  if (tags?.some(t => t.name.toLowerCase().includes(word))) return 1;
  if (desc.includes(word)) return 0.5;
  return 0;
}

function score(name: string, aliases: string[], tags: { name: string }[] | undefined, description: any, q: string): number {
  const names = [name.toLowerCase(), ...aliases.map(a => a.toLowerCase()),];
  const desc = extractText(description);
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 0;
  if (words.length === 1) return scoreWord(words[0], names, tags, desc);
  let min = Infinity;
  for (const word of words) {
    const s = scoreWord(word, names, tags, desc);
    if (s === 0) return 0;
    if (s < min) min = s;
  }
  return min;
}

export type OverlayResult =
  | { type: `video`; item: VideoDisplay; score: number }
  | { type: `model`; item: ModelDisplay; score: number };

export function getOverlayResults(
  allVideos: VideoDisplay[],
  allModels: ModelDisplay[],
  query: string,
  typeFilter: `all` | `video` | `model`
): OverlayResult[] {
  const results: OverlayResult[] = [];

  if (typeFilter !== `model`) {
    for (const { item, score, } of filterBySearch(allVideos, v => v.title, () => [], v => v.description, query, [])) {
      results.push({ type: `video`, item, score, });
    }
  }
  if (typeFilter !== `video`) {
    for (const { item, score, } of filterBySearch(allModels, m => m.name, m => m.aliases, m => m.description, query, [])) {
      results.push({ type: `model`, item, score, });
    }
  }

  return results.sort((a, b) => b.score - a.score);
}

export function deleteDuplicateModels(options: StrapiOptions, elements: ModelDisplay[]) {
  // Delete duplicates when sorting by relation
  // https://github.com/strapi/strapi/issues/11892
  if (options?.sort?.find((s: string) => s.startsWith(`videos.`))) {
    return elements.filter((value, index, self) =>
      index === self.findIndex((t) => t.slug === value.slug)
    );
  }

  return elements;
}
